import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  Coffee,
  Plus,
  Edit2,
  Loader2,
  Check,
  X,
  Clock,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';

/**
 * BreakTypesManager - admin UI for managing company break types.
 *
 * Lists all break types, allows adding new ones, inline editing,
 * and toggling active/inactive status.
 *
 * @param {string} companyId - UUID of the company
 */
export default function BreakTypesManager({ companyId }) {
  const [breakTypes, setBreakTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  // New break type form state
  const [newName, setNewName] = useState('');
  const [newIsPaid, setNewIsPaid] = useState(false);
  const [newDuration, setNewDuration] = useState('15');

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editIsPaid, setEditIsPaid] = useState(false);
  const [editDuration, setEditDuration] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const editNameRef = useRef(null);
  const newNameRef = useRef(null);

  // ---- Data Fetching ----

  const fetchBreakTypes = useCallback(async () => {
    if (!companyId) return;

    try {
      const { data, error } = await supabase
        .from('break_types')
        .select('*')
        .eq('company_id', companyId)
        .order('sort_order', { ascending: true })
        .order('created_date', { ascending: true });

      if (error) throw error;
      setBreakTypes(data || []);
    } catch (err) {
      console.error('Failed to fetch break types:', err);
      toast.error('Failed to load break types');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchBreakTypes();
  }, [fetchBreakTypes]);

  // Focus the name input when adding or editing
  useEffect(() => {
    if (adding && newNameRef.current) {
      newNameRef.current.focus();
    }
  }, [adding]);

  useEffect(() => {
    if (editingId && editNameRef.current) {
      editNameRef.current.focus();
      editNameRef.current.select();
    }
  }, [editingId]);

  // ---- Add Break Type ----

  const handleStartAdding = () => {
    setAdding(true);
    setNewName('');
    setNewIsPaid(false);
    setNewDuration('15');
  };

  const handleCancelAdding = () => {
    setAdding(false);
    setNewName('');
    setNewIsPaid(false);
    setNewDuration('15');
  };

  const handleAddBreakType = async () => {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      toast.error('Please enter a break type name');
      return;
    }

    const duration = parseInt(newDuration, 10);
    if (isNaN(duration) || duration < 1 || duration > 480) {
      toast.error('Duration must be between 1 and 480 minutes');
      return;
    }

    setSaving(true);
    try {
      // Determine the next sort order
      const maxSort = breakTypes.reduce((max, bt) => Math.max(max, bt.sort_order || 0), 0);

      const { error } = await supabase
        .from('break_types')
        .insert({
          company_id: companyId,
          name: trimmedName,
          is_paid: newIsPaid,
          default_duration_minutes: duration,
          sort_order: maxSort + 1,
          is_active: true,
        });

      if (error) throw error;

      toast.success(`"${trimmedName}" break type added`);
      setAdding(false);
      setNewName('');
      setNewIsPaid(false);
      setNewDuration('15');
      fetchBreakTypes();
    } catch (err) {
      console.error('Failed to add break type:', err);
      toast.error('Failed to add break type');
    } finally {
      setSaving(false);
    }
  };

  const handleAddKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddBreakType();
    }
    if (e.key === 'Escape') {
      handleCancelAdding();
    }
  };

  // ---- Inline Editing ----

  const handleStartEditing = (breakType) => {
    setEditingId(breakType.id);
    setEditName(breakType.name || '');
    setEditIsPaid(breakType.is_paid || false);
    setEditDuration(String(breakType.default_duration_minutes || 15));
  };

  const handleCancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditIsPaid(false);
    setEditDuration('');
  };

  const handleSaveEdit = async () => {
    const trimmedName = editName.trim();
    if (!trimmedName) {
      toast.error('Break type name cannot be empty');
      return;
    }

    const duration = parseInt(editDuration, 10);
    if (isNaN(duration) || duration < 1 || duration > 480) {
      toast.error('Duration must be between 1 and 480 minutes');
      return;
    }

    setEditSaving(true);
    try {
      const { error } = await supabase
        .from('break_types')
        .update({
          name: trimmedName,
          is_paid: editIsPaid,
          default_duration_minutes: duration,
        })
        .eq('id', editingId);

      if (error) throw error;

      toast.success('Break type updated');
      setEditingId(null);
      fetchBreakTypes();
    } catch (err) {
      console.error('Failed to update break type:', err);
      toast.error('Failed to update break type');
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    }
    if (e.key === 'Escape') {
      handleCancelEditing();
    }
  };

  // ---- Toggle Active ----

  const handleToggleActive = async (breakType) => {
    const newIsActive = !breakType.is_active;

    try {
      const { error } = await supabase
        .from('break_types')
        .update({ is_active: newIsActive })
        .eq('id', breakType.id);

      if (error) throw error;

      toast.success(
        newIsActive
          ? `"${breakType.name}" reactivated`
          : `"${breakType.name}" deactivated`
      );
      fetchBreakTypes();
    } catch (err) {
      console.error('Failed to toggle break type:', err);
      toast.error('Failed to update break type');
    }
  };

  // ---- Render ----

  const activeBreakTypes = breakTypes.filter((bt) => bt.is_active);
  const inactiveBreakTypes = breakTypes.filter((bt) => !bt.is_active);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coffee className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-base">Break Types</CardTitle>
          </div>
          {!adding && (
            <Button size="sm" onClick={handleStartAdding}>
              <Plus className="h-4 w-4" />
              Add Break Type
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Add New Break Type Form */}
            <AnimatePresence>
              {adding && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/50 p-3 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Input
                          ref={newNameRef}
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          onKeyDown={handleAddKeyDown}
                          placeholder="Break type name..."
                          className="h-8 text-sm"
                          disabled={saving}
                        />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                          type="number"
                          value={newDuration}
                          onChange={(e) => setNewDuration(e.target.value)}
                          onKeyDown={handleAddKeyDown}
                          className="h-8 w-20 text-sm text-center"
                          min={1}
                          max={480}
                          disabled={saving}
                        />
                        <span className="text-xs text-muted-foreground">min</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={newIsPaid}
                          onCheckedChange={setNewIsPaid}
                          disabled={saving}
                        />
                        <span className="text-sm text-muted-foreground">
                          {newIsPaid ? 'Paid break' : 'Unpaid break'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelAdding}
                          disabled={saving}
                          className="h-7 px-2"
                        >
                          <X className="h-3.5 w-3.5" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddBreakType}
                          disabled={saving || !newName.trim()}
                          className="h-7 px-3"
                        >
                          {saving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Active Break Types */}
            {activeBreakTypes.length === 0 && inactiveBreakTypes.length === 0 && !adding && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-3">
                  <Coffee className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-muted-foreground">
                  No break types configured
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Add break types to allow employees to log breaks during their shifts.
                </p>
              </div>
            )}

            {activeBreakTypes.map((bt) => (
              <motion.div
                key={bt.id}
                layout
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
              >
                {editingId === bt.id ? (
                  /* Editing Mode */
                  <div className="rounded-lg border-2 border-blue-300 bg-blue-50/30 p-3 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Input
                          ref={editNameRef}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={handleEditKeyDown}
                          className="h-8 text-sm"
                          disabled={editSaving}
                        />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                          type="number"
                          value={editDuration}
                          onChange={(e) => setEditDuration(e.target.value)}
                          onKeyDown={handleEditKeyDown}
                          className="h-8 w-20 text-sm text-center"
                          min={1}
                          max={480}
                          disabled={editSaving}
                        />
                        <span className="text-xs text-muted-foreground">min</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={editIsPaid}
                          onCheckedChange={setEditIsPaid}
                          disabled={editSaving}
                        />
                        <span className="text-sm text-muted-foreground">
                          {editIsPaid ? 'Paid break' : 'Unpaid break'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelEditing}
                          disabled={editSaving}
                          className="h-7 px-2"
                        >
                          <X className="h-3.5 w-3.5" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={editSaving || !editName.trim()}
                          className="h-7 px-3"
                        >
                          {editSaving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Display Mode */
                  <div className="rounded-lg border p-3 hover:bg-muted/30 transition-colors group">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-orange-100">
                          <Coffee className="h-4 w-4 text-orange-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate">
                              {bt.name}
                            </span>
                            <Badge
                              variant={bt.is_paid ? 'success' : 'secondary'}
                              className="text-[10px] px-1.5 py-0"
                            >
                              {bt.is_paid ? 'Paid' : 'Unpaid'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {bt.default_duration_minutes} min default
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleStartEditing(bt)}
                          title="Edit break type"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleToggleActive(bt)}
                          title="Deactivate break type"
                        >
                          <ToggleRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}

            {/* Inactive Break Types */}
            {inactiveBreakTypes.length > 0 && (
              <div className="pt-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  Inactive
                </p>
                {inactiveBreakTypes.map((bt) => (
                  <motion.div
                    key={bt.id}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="rounded-lg border border-dashed p-3 opacity-60 hover:opacity-100 transition-opacity group mb-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-gray-100">
                            <Coffee className="h-4 w-4 text-gray-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate text-muted-foreground">
                                {bt.name}
                              </span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                {bt.is_paid ? 'Paid' : 'Unpaid'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                {bt.default_duration_minutes} min default
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-green-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleToggleActive(bt)}
                          title="Reactivate break type"
                        >
                          <ToggleLeft className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
